import { useEffect } from 'react';
import { setDatadogRumUser } from '@/datadog';
import { UserGetResponse } from '@lio/api-types';
import { useStore } from '@store/GlobalStore';
import { UserRoles } from '@models/user';
import { useCurrentUser } from '@services/user/useCurrentUser';
import { IUserRole } from '@store/types/UserStoreSlice.type';
import { shallow } from 'zustand/shallow';
import { GridColumn, FontSize, TWalkthroughs } from '@store/types/UserStoreSlice.type';
import { OfferingThemeMode } from '@store/types/UserStoreSlice.type';
import { ClockFormat } from '@lio/rule-engine';

export type UserDisplayPreferences = {
  clockFormat?: ClockFormat | null | undefined;
  fontSize?: FontSize | null | undefined;
  offeringColorMode?: OfferingThemeMode | null | undefined;
  offeringGridColumns?: GridColumn | null | undefined;
  walkthroughs?: TWalkthroughs | null | undefined;
};

export const getUserRolesFromProfile = (selectedUser?: UserGetResponse): string[] => {
  if (!selectedUser) {
    return [];
  }

  let roles: string[] = [];

  if (selectedUser.lioUsers.length > 0) {
    roles = selectedUser.lioUsers.map((user) => user.role);
  } else if (selectedUser.brokerageUsers.length > 0) {
    roles = selectedUser.brokerageUsers.map((user) => user.role);
  }
  if (selectedUser.isCustomerUser) {
    roles.push(UserRoles.Quoter);
  }

  return roles;
};

export const useUserRoles = () => {
  const { currentUser, isError, isFetched, isInitialLoading, isAuthenticated } = useCurrentUser();
  const { firstName, lastName, email, brokerageUsers } = currentUser || ({} as UserGetResponse);
  const setUserRoles = useStore((state) => state.setUserRoles, shallow);
  const [layoutPreferences, setLayoutPreferences, setWalkthroughs] = useStore(
    (state) => [state.layoutPreferences, state.setLayoutPreferences, state.setWalkthroughs],
    shallow,
  );

  const getIUserRoleObj = (role: string[]): IUserRole => ({
    payerAccountType: currentUser?.payer?.accountTypes ?? [],
    hasPayerProfile: !!currentUser?.payer?.id,
    isAdmin: role.includes(UserRoles.SystemAdmin),
    isPortalAdmin: role.includes(UserRoles.PortalAdmin),
    isUnderwriter: role.includes(UserRoles.Underwriter),
    isLIOContractor: role.includes(UserRoles.LIOContractor),
    isBroker: role.includes(UserRoles.Producer),
    isJrOps: role.includes(UserRoles.JrOps),
    isSrOps: role.includes(UserRoles.SrOps),
    isCSR: role.includes(UserRoles.CSR),
    isLioSales: role.includes(UserRoles.LioSales),
    // internal user roles
    isInternalUser:
      role.includes(UserRoles.SystemAdmin) ||
      role.includes(UserRoles.Underwriter) ||
      role.includes(UserRoles.LIOContractor) ||
      role.includes(UserRoles.JrOps) ||
      role.includes(UserRoles.SrOps) ||
      role.includes(UserRoles.LioSales) ||
      role.includes(UserRoles.PortalAdmin),
    isInternalWithoutSales:
      role.includes(UserRoles.SystemAdmin) ||
      role.includes(UserRoles.Underwriter) ||
      role.includes(UserRoles.LIOContractor) ||
      role.includes(UserRoles.JrOps) ||
      role.includes(UserRoles.SrOps) ||
      role.includes(UserRoles.PortalAdmin),
    isCustomerUser: currentUser?.isCustomerUser ?? false,
    canOverrideIssuingCompany: currentUser?.underwriter?.canOverrideIssuingCompany ?? false,
  });

  const getUserRoles = (currentUser: UserGetResponse) => {
    let roles: string[] = [];

    if (currentUser.lioUsers.length > 0) {
      roles = currentUser.lioUsers.map((user) => user.role);
    } else if (currentUser.brokerageUsers.length > 0) {
      roles = currentUser.brokerageUsers.map((user) => user.role);
    }
    if (currentUser.isCustomerUser) {
      // NOTE: once 17179 is completed & lio-rules has type we skip this type bypass
      roles.push(UserRoles.Quoter);
    }

    return { roles, userRole: getIUserRoleObj(roles) };
  };

  useEffect(() => {
    const isLoading = !isFetched ? true : isInitialLoading;
    if (currentUser) {
      const { roles, userRole } = getUserRoles(currentUser);
      setUserRoles({
        currentUser,
        roles,
        userAuth: {
          payerAccountType: userRole.payerAccountType,
          hasPayerProfile: userRole.hasPayerProfile,
          isCustomerUser: userRole.isCustomerUser,
          isAdmin: userRole.isAdmin,
          isPortalAdmin: userRole.isPortalAdmin,
          isUnderwriter: userRole.isUnderwriter,
          isLIOContractor: userRole.isLIOContractor,
          isBroker: userRole.isBroker,
          isJrOps: userRole.isJrOps,
          isSrOps: userRole.isSrOps,
          isCSR: userRole.isCSR,
          isLioSales: userRole.isLioSales,
          isInternalUser: userRole.isInternalUser,
          isInternalWithoutSales: userRole.isInternalWithoutSales,
          canOverrideIssuingCompany: userRole.canOverrideIssuingCompany,
          isAuthenticated: isAuthenticated ? true : false,
          isLoading,
          isError,
        },
      });

      // Load all display preferences from the API response
      const displayPrefs: UserDisplayPreferences | undefined = currentUser.displayPreferences as
        | UserDisplayPreferences
        | undefined;
      setLayoutPreferences({
        ...layoutPreferences,
        clockFormat: displayPrefs?.clockFormat ?? ClockFormat.TwelveHour,
        offeringGridColumns: displayPrefs?.offeringGridColumns ?? GridColumn.OFFERING_DEFAULT,
        fontSize: displayPrefs?.fontSize ?? FontSize.DEFAULT,
        offeringColorMode: displayPrefs?.offeringColorMode ?? OfferingThemeMode.DEFAULT,
      });
      setWalkthroughs(displayPrefs?.walkthroughs ?? {});
      setDatadogRumUser({
        id: currentUser.id,
        name: `${firstName} ${lastName}`,
        email: email as string,
        roles: roles.join(', '),
        brokerageIds: brokerageUsers.map((bu) => bu.brokerageId).join(', '),
      });
    } else {
      setUserRoles({ roles: [], userAuth: { isLoading, isError, isAuthenticated: false } });
    }
    // eslint-disable-next-line
  }, [brokerageUsers, currentUser, isInitialLoading, isFetched, firstName, lastName, email]);
};
